import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

export type CheckStatus = "GREEN" | "AMBER" | "RED" | "NA";

export type Check = {
  readonly key: string;
  readonly title: string;
  readonly status: string;
  readonly detail: string | null | undefined;
  readonly actual: string | null | undefined;
  readonly target: string | null | undefined;
  readonly actionPath: string | null | undefined;
  readonly actionLabel: string | null | undefined;
};

export type Group = {
  readonly code: string;
  readonly title: string;
  readonly greenCount: number;
  readonly totalCount: number;
  readonly checks: ReadonlyArray<Check>;
};

export type Score = {
  readonly green: number;
  readonly amber: number;
  readonly red: number;
  readonly total: number;
};

const DOT: Record<string, string> = {
  GREEN: "bg-emerald-500",
  AMBER: "bg-amber-400",
  RED: "bg-red-500",
  NA: "bg-gray-200",
};

const ROW: Record<string, string> = {
  GREEN: "text-gray-500",
  AMBER: "text-gray-900",
  RED: "text-gray-900",
  NA: "text-gray-300",
};

// The bar reads as one line of the account's readiness: how much is done, how
// much needs a decision, how much is broken. N/A never appears — it is not
// progress withheld, it is a check that does not apply.
function ScoreBar({ score }: { score: Score }) {
  const pct = (n: number) => (score.total === 0 ? 0 : (n / score.total) * 100);

  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
      <div className="bg-emerald-500" style={{ width: `${pct(score.green)}%` }} />
      <div className="bg-amber-400" style={{ width: `${pct(score.amber)}%` }} />
      <div className="bg-red-500" style={{ width: `${pct(score.red)}%` }} />
    </div>
  );
}

function CheckRow({ check }: { check: Check }) {
  const na = check.status === "NA";

  return (
    <li className="flex items-baseline gap-3 py-1.5">
      <span
        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT[check.status] ?? DOT.NA}`}
        aria-hidden
      />
      <span className="w-10 shrink-0 font-mono text-[10px] uppercase text-gray-300">
        {check.key}
      </span>

      <span className="min-w-0 flex-1">
        <span className={`text-sm ${ROW[check.status] ?? ROW.NA}`}>{check.title}</span>
        {na && <span className="ml-2 text-xs text-gray-300">not applicable</span>}
        {!na && check.detail && (
          <span className="block text-xs text-gray-500">{check.detail}</span>
        )}
      </span>

      {!na && check.actual != null && (
        <span className="shrink-0 whitespace-nowrap font-mono text-xs tabular-nums text-gray-400">
          {check.actual}
          {check.target != null && (
            <span className="text-gray-300"> / {check.target}</span>
          )}
        </span>
      )}

      {!na && check.status !== "GREEN" && check.actionPath && (
        <a
          href={check.actionPath}
          className="shrink-0 whitespace-nowrap text-xs font-medium text-blue-600 hover:underline"
        >
          {check.actionLabel ?? "Fix"} →
        </a>
      )}
    </li>
  );
}

// Groups start collapsed once they are fully green: a finished domain is
// reference, not work. Anything with an outstanding check opens itself, so the
// page lands on exactly what is left to do.
function GroupSection({ group }: { group: Group }) {
  const done = group.totalCount > 0 && group.greenCount === group.totalCount;
  const [open, setOpen] = useState(!done);
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="border-b last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 py-2 text-left"
      >
        <Chevron className="h-3.5 w-3.5 shrink-0 text-gray-400" />
        <span className="font-mono text-[10px] uppercase text-gray-300">
          {group.code}
        </span>
        <span className="flex-1 text-sm font-medium text-gray-900">
          {group.title}
        </span>
        <span
          className={`font-mono text-xs tabular-nums ${
            done ? "text-emerald-600" : "text-gray-400"
          }`}
        >
          {group.greenCount}/{group.totalCount}
        </span>
      </button>

      {open && (
        <ul className="mb-2 divide-y divide-gray-50 pl-6">
          {group.checks.map((check) => (
            <CheckRow key={check.key} check={check} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default function PreflightChecklist({
  groups,
  score,
}: {
  groups: ReadonlyArray<Group>;
  score: Score | null | undefined;
}) {
  const outstanding = score ? score.total - score.green : 0;

  return (
    <div className="rounded border bg-white p-4">
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <div>
          <h2 className="text-sm font-medium text-gray-700">Pre-flight checklist</h2>
          <p className="text-xs text-gray-500">
            {score == null
              ? "Nothing to check yet."
              : outstanding === 0
              ? "Everything that applies to this account is done — the data is fully auditable."
              : `${outstanding} action${outstanding === 1 ? "" : "s"} left before the data is fully auditable.`}
          </p>
        </div>

        {score && (
          <div className="shrink-0 text-right">
            <div className="font-mono text-lg tabular-nums text-gray-900">
              {score.green}
              <span className="text-gray-300">/{score.total}</span>
            </div>
            <div className="text-xs text-gray-400">
              {score.red > 0 && <span className="text-red-600">{score.red} blocking</span>}
              {score.red > 0 && score.amber > 0 && " · "}
              {score.amber > 0 && <span className="text-amber-600">{score.amber} to decide</span>}
            </div>
          </div>
        )}
      </div>

      {score && <ScoreBar score={score} />}

      <div className="mt-3">
        {groups.map((group) => (
          <GroupSection key={group.code} group={group} />
        ))}
      </div>
    </div>
  );
}
